import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User as UserIcon, Loader2, ShieldCheck, X, Users } from "lucide-react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { login, register, adminLogin } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", emoji: "", tag: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);

  const doAdmin = async (e) => {
    e.preventDefault();
    setAdminBusy(true);
    try {
      await adminLogin(passcode);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect passcode");
    } finally {
      setAdminBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && (!form.emoji || !form.tag.trim())) {
      toast.error("Pick an emoji and up to 4 characters for your avatar.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.email, form.password, null, form.name, { emoji: form.emoji, tag: form.tag.trim() });
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md sms-fade-up">
        <div className="flex justify-center mb-8">
          <Logo size="lg" tagline />
        </div>
        <div className="sms-tweed rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] p-7 sms-fade-up" data-testid="login-card">
          <h1 className="text-2xl font-extrabold text-center mb-1 text-white">
            {mode === "login" ? "Login to Learn" : "Create your account"}
          </h1>
          <p className="text-center text-sm text-white/55 mb-6">
            {mode === "login" ? "Browse and learn, free" : "Start turning goals into steps."}
          </p>

          <button
            data-testid="google-login-btn"
            onClick={google}
            className="w-full flex items-center justify-center gap-3 border-2 border-[#5d8a52] bg-black rounded-2xl py-3 font-semibold text-white hover:bg-[#5d8a52]/15 transition-colors mb-5"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-white/15" />
            <span className="text-xs text-white/40 font-medium">or with email</span>
            <div className="h-px flex-1 bg-white/15" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div>
                <div className="relative">
                  <UserIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    data-testid="fullname-input"
                    className="w-full rounded-2xl bg-black text-white placeholder:text-white/40 pl-11 pr-4 py-3.5 outline-none border-2 border-[#5d8a52]"
                    placeholder="Your full name"
                    maxLength={80}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <p className="text-xs text-white/45 mt-1.5 px-1">
                  Kept private on your account — never shown publicly.
                </p>
              </div>
            )}
            {mode === "register" && (
              <div data-testid="avatar-signup-field" className="rounded-2xl border-2 border-[#5d8a52] bg-black/40 p-3.5">
                <label className="text-[11px] font-bold text-[#a9d79b] uppercase tracking-wide">Your community avatar</label>
                {/* Live preview — arcade high-score style */}
                <div className="flex items-center justify-center my-2.5">
                  <div
                    data-testid="signup-avatar-preview"
                    className="inline-flex items-center gap-1 bg-black/85 text-white font-extrabold rounded-full px-4 py-2 text-xl shadow-md"
                  >
                    <span>{form.emoji || "🙂"}</span>
                    <span className="tabular-nums tracking-widest">{form.tag || "____"}</span>
                  </div>
                </div>
                <input
                  data-testid="avatar-tag-input"
                  value={form.tag}
                  onChange={(e) => setForm({ ...form, tag: e.target.value.slice(0, 4) })}
                  maxLength={4}
                  placeholder="Your 4 characters — e.g. 45k$, Art7, ★★★★"
                  className="w-full rounded-xl bg-black text-white placeholder:text-white/40 px-3 py-2.5 text-sm font-bold tracking-widest text-center outline-none"
                  style={{ border: "2px solid #5d8a52" }}
                  required
                />
                <div className="mt-2.5 flex justify-center rounded-xl overflow-hidden" data-testid="signup-emoji-picker">
                  <Picker
                    data={data}
                    onEmojiSelect={(em) => setForm({ ...form, emoji: em.native })}
                    theme="dark"
                    previewPosition="none"
                    skinTonePosition="none"
                    navPosition="top"
                    autoFocus={false}
                    perLine={8}
                  />
                </div>
                <p className="text-xs text-white/55 mt-2 leading-relaxed">
                  Pick an emoji + up to 4 characters — letters, numbers or symbols, like the arcade high-score days (e.g. 🌞45k$). This is your public identity on every video you post. We never reveal your real name or location.
                </p>
              </div>
            )}
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="email-input"
                type="email"
                required
                className="w-full rounded-2xl bg-black text-white placeholder:text-white/40 pl-11 pr-4 py-3.5 outline-none border-2 border-[#5d8a52]"
                placeholder="you@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="password-input"
                type="password"
                required
                minLength={6}
                className="w-full rounded-2xl bg-black text-white placeholder:text-white/40 pl-11 pr-4 py-3.5 outline-none border-2 border-[#5d8a52]"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            {mode === "login" && (
              <div className="text-right -mt-1 mb-1">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm font-semibold text-[#5d8a52] hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}
            <button
              data-testid="submit-auth-btn"
              type="submit"
              disabled={busy}
              className="sms-solid-btn w-full text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {busy && <Loader2 size={18} className="spin" />}
              {mode === "login" ? "Log in" : "Sign up"}
            </button>
          </form>

          <p className="text-center text-sm text-white/55 mt-5">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-bold text-[#a9d79b] hover:underline"
            >
              {mode === "login" ? "Sign up" : "Log in"}
            </button>
          </p>

          <div className="mt-5 pt-4 border-t border-white/10 text-center">
            <button
              data-testid="browse-community-btn"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-[#a9d79b] hover:underline"
            >
              <Users size={16} /> Browse the Community — no account needed
            </button>
          </div>
        </div>
        <div className="mt-4 text-center text-xs font-semibold text-[#9a96ac]">
          <button data-testid="login-privacy-link" onClick={() => navigate("/privacy")} className="hover:text-[#5d8a52] transition-colors">Privacy Policy</button>
          <span className="mx-2 text-[#d6d3e0]">·</span>
          <button data-testid="login-terms-link" onClick={() => navigate("/terms")} className="hover:text-[#5d8a52] transition-colors">Terms of Service</button>
        </div>
      </div>

      {/* TEMPORARY admin bypass — invisible hotspot (cursor turns to pointer) */}
      <button
        data-testid="admin-btn"
        aria-label=" "
        onClick={() => { setPasscode(""); setShowAdmin(true); }}
        className="fixed bottom-0 left-0 w-14 h-14 z-50 cursor-pointer bg-transparent border-0 p-0 m-0"
        style={{ outline: "none" }}
      />

      {showAdmin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#171426]/50 backdrop-blur-sm p-4"
          onClick={() => setShowAdmin(false)}
          data-testid="admin-modal-overlay"
        >
          <form
            onSubmit={doAdmin}
            onClick={(e) => e.stopPropagation()}
            className="sms-tweed rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] w-full max-w-xs p-6 sms-fade-up"
            data-testid="admin-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-extrabold flex items-center gap-2 text-white"><ShieldCheck size={18} className="text-[#a9d79b]" /> Admin access</h2>
              <button type="button" onClick={() => setShowAdmin(false)} className="text-white/50 hover:text-white"><X size={18} /></button>
            </div>
            <input
              data-testid="admin-passcode-input"
              type="password"
              autoFocus
              autoComplete="off"
              maxLength={40}
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Admin passcode"
              className="w-full rounded-2xl bg-black text-white placeholder:text-white/40 px-4 py-3 text-center text-base outline-none border-2 border-[#5d8a52]"
            />
            <button
              data-testid="admin-submit-btn"
              type="submit"
              disabled={adminBusy}
              className="sms-solid-btn w-full text-white font-bold py-3 rounded-2xl mt-3 flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {adminBusy && <Loader2 size={16} className="spin" />} Enter
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
